import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDSButton } from '@gehc-eds-react/eds-button';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';
import { apiFetch } from '../../utils/apiClient';
import { addErrorNotification, addSuccessNotification } from '../../redux/slices/notificationSlice';
import { useAppDispatch } from '../../redux/hooks';
import { tokens, card, cardHeader } from '../../constants/Designtokens';
 
const t = tokens;
 
const formatDate = (value) => {
    if (!value) return '--';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};
 
const StatusBadge = ({ status }) => {
    const tone = status === 'Partial'
        ? { color: t.color.warning, backgroundColor: t.color.warningTint }
        : status === 'Failed' || status === 'Error'
            ? { color: t.color.danger, backgroundColor: t.color.dangerTint }
            : { color: t.color.success, backgroundColor: t.color.successTint };
    return (
        <span
            style={{
                ...tone,
                fontSize: '12px',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: '999px',
                marginLeft: '10px',
                verticalAlign: 'middle'
            }}
        >
            {status}
        </span>
    );
};
 
// Placeholder used only for fields the /jobs/{job_id} response doesn't return at all
// (retry count/max, and per-file failure detail when the API gives none).
const FALLBACK_JOB = {
    id: 'b7e1a4f9-2c3d-4e5f-9a1b-7d6c5e4f3a2b',
    status: 'Partial',
    sourceBucket: 'vendor-bucket-02',
    createdAt: '2026-06-18T07:02:00Z',
    updatedAt: '2026-06-18T09:47:00Z',
    createdBy: 'j.smith@ge.com',
    retryCount: 1,
    retryMax: 5,
    total: 600,
    copied: 498,
    failedCount: 102
};
 
// Maps the real `/api/v1/s3-batch/jobs/{job_id}` response shape ({ audit, live }) into the
// view model this page renders. Any field the API doesn't provide falls back
// to FALLBACK_JOB so the page never breaks while the contract is still settling.
const buildJobViewModel = (response, fallbackId) => {
    const fallback = { ...FALLBACK_JOB, id: fallbackId || FALLBACK_JOB.id };
 
    if (!response || (!response.audit && !response.live)) {
        return fallback;
    }
 
    const audit = response.audit || {};
    const live = response.live || {};
    const progress = live.progress || {};
    const failureReasons = Array.isArray(live.failure_reasons) ? live.failure_reasons : [];
    const failedCount = progress.failed ?? fallback.failedCount;
    const failureReason = failureReasons
        .map((reason) => reason?.FailureReason || reason?.FailureCode || reason?.message || reason?.reason || reason?.error || reason?.code || reason?.object_key)
        .filter(Boolean)
        .join(', ');
    const nextSteps = [
        live?.next_steps,
        response?.next_steps,
        audit?.next_steps,
        ...failureReasons.map((reason) => reason?.next_steps)
    ]
        .flatMap((value) => {
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') return [value];
            return [];
        })
        .filter((step) => typeof step === 'string' && step.trim().length > 0);
 
    return {
        id: audit.job_id || fallbackId || fallback.id,
        status: live.status || audit.status || fallback.status,
        sourceBucket: audit.source_bucket || fallback.sourceBucket,
        createdAt: live.creation_time || audit.submitted_at || fallback.createdAt,
        updatedAt: live.termination_date || live.creation_time || fallback.updatedAt,
        createdBy: audit.submitted_by || fallback.createdBy,
        // Not part of this API response yet — keep the dummy values until retry
        // tracking is exposed by the backend.
        retryCount: fallback.retryCount,
        retryMax: fallback.retryMax,
        total: progress.total ?? fallback.total,
        copied: progress.succeeded ?? fallback.copied,
        failedCount,
        failureReason,
        nextSteps
    };
};
 
const JobDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { jobId } = useParams();
    const dispatch = useAppDispatch();
 
    const resolvedJobId = jobId || location.state?.job?.id
 
    const [jobResponse, setJobResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
 
    const job = useMemo(
        () => buildJobViewModel(jobResponse, resolvedJobId),
        [jobResponse, resolvedJobId]
    );
 
    const [isRetryingJob, setIsRetryingJob] = useState(false);
    const [isCancellingJob, setIsCancellingJob] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
 
    const failedTotal = job.failedCount;
    const copiedPct = job.total > 0 ? Math.round((job.copied / job.total) * 100) : 0;
    const failedPct = job.total > 0 ? Math.round((failedTotal / job.total) * 100) : 0;
    const normalizedStatus = String(job.status || '').toLowerCase();
    const isActiveJob = normalizedStatus === 'active';
    const isRetryInProgress = isRetryingJob;
    const isFailedLikeStatus = normalizedStatus === 'failed' || normalizedStatus === 'error';
    const showFailureReason = isFailedLikeStatus || failedTotal > 0;
    const failureReasonText = job.failureReason || 'Failure reason unavailable';
    const nextSteps = Array.isArray(job.nextSteps) ? job.nextSteps : [];
 
    const loadJobDetail = useCallback(async ({ showLoader = false } = {}) => {
        if (showLoader) {
            setIsLoading(true);
        }
        setLoadError('');
        try {
            const response = await apiFetch(`/api/v1/s3-batch/jobs/${resolvedJobId}`, {
                method: 'GET',
                retry: false
            });
            setJobResponse(response);
        } catch (error) {
            setJobResponse(null);
            setLoadError(error.message || 'Failed to load job details.');
            dispatch(addErrorNotification({ message: 'Failed to load job details. Showing unavailable data as placeholders.' }));
        } finally {
            if (showLoader) {
                setIsLoading(false);
            }
        }
    }, [dispatch, resolvedJobId]);

    useEffect(() => {
        let isCancelled = false;

        const run = async () => {
            if (isCancelled) return;
            await loadJobDetail({ showLoader: true });
        };

        run();
        return () => {
            isCancelled = true;
        };
    }, [loadJobDetail]);
 
    const goToJobs = () => navigate('/jobs');

    const refreshJobDetails = async () => {
        setIsRefreshing(true);
        try {
            await loadJobDetail({ showLoader: false });
        } finally {
            setIsRefreshing(false);
        }
    };
 
    const retryJob = async () => {
        setIsRetryingJob(true);
        try {
            await apiFetch(`/api/v1/s3-batch/jobs/${job.id}/retry`, {
                method: 'POST',
                retry: false
            });
            dispatch(addSuccessNotification({ message: `Retry started for job "${job.id}".` }));
        } catch (error) {
            dispatch(addErrorNotification({ message: 'Failed to start retry. Please try again.' }));
            console.error('Retry job error:', error);
        } finally {
            setIsRetryingJob(false);
        }
    };

    const cancelJob = async () => {
        setIsCancellingJob(true);
        try {
            await apiFetch(`/api/v1/s3-batch/jobs/${job.id}/cancel`, {
                method: 'POST',
                retry: false
            });
            dispatch(addSuccessNotification({ message: `Cancel requested for job "${job.id}".` }));
        } catch (error) {
            dispatch(addErrorNotification({ message: 'Failed to cancel job. Please try again.' }));
            console.error('Cancel job error:', error);
        } finally {
            setIsCancellingJob(false);
        }
    };
 
    // While the real job detail is still in flight, show a loader instead of
    // rendering dummy/fallback data.
    if (isLoading) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: t.color.canvas, fontFamily: t.font.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                    <div
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            border: `3px solid ${t.color.border}`,
                            borderTopColor: t.color.primary,
                            animation: 'job-details-spin 0.8s linear infinite'
                        }}
                    />
                    <span style={{ fontSize: '13.5px', color: t.color.textSecondary, fontWeight: 600 }}>
                        Loading job details...
                    </span>
                    <style>{`@keyframes job-details-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            </div>
        );
    }
 
    return (
        <div style={{ minHeight: '100vh', backgroundColor: t.color.canvas, fontFamily: t.font.body, display: 'flex' }}>
            <SpinnerComponent isVisible={isRefreshing} overlay={true} message="Refreshing job details..." />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Top bar */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 32px',
                        backgroundColor: t.color.surface,
                        borderBottom: `1px solid ${t.color.border}`
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                            style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '8px',
                                backgroundColor: t.color.primary,
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            GE
                        </div>
                        <span style={{ fontWeight: 700, fontSize: '15px', color: t.color.textPrimary }}>GE HealthCare</span>
                        <span style={{ fontSize: '15px', color: t.color.textSecondary }}>Data ingestion</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: t.radius.sm, border: `1px solid ${t.color.border}` }} />
                        <div style={{ width: '32px', height: '32px', borderRadius: t.radius.sm, border: `1px solid ${t.color.border}` }} />
                    </div>
                </div>
 
                {/* Page content */}
                <div style={{ padding: '28px 32px', maxWidth: '1180px', width: '100%', margin: '0 auto' }}>
                    {/* Breadcrumb */}
                    <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                        <span
                            onClick={goToJobs}
                            style={{ color: t.color.primary, fontWeight: 600, cursor: 'pointer' }}
                        >
                            Jobs
                        </span>
                        <span style={{ color: t.color.textMuted }}> / {job.id}</span>
                    </div>
 
                    {loadError && (
                        <div
                            style={{
                                ...card,
                                borderColor: t.color.danger,
                                backgroundColor: t.color.dangerTint,
                                color: t.color.danger,
                                padding: '12px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '18px'
                            }}
                        >
                            Couldn&apos;t load live job data ({loadError}). Showing placeholder values where real data is unavailable.
                        </div>
                    )}

                    {/* Title row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: t.color.textPrimary, fontFamily: t.font.mono }}>
                                    {job.id}
                                </span>
                                <StatusBadge status={job.status} />
                                {isLoading && (
                                    <span style={{ fontSize: '12px', color: t.color.textMuted, marginLeft: '10px' }}>Loading...</span>
                                )}
                            </div>
                            <div style={{ fontSize: '13.5px', color: t.color.textSecondary, marginTop: '4px' }}>
                                {job.sourceBucket} &middot; {formatDate(job.createdAt)}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={refreshJobDetails}
                                aria-label="Refresh job details"
                                title="Refresh job details"
                                disabled={isRefreshing || isActiveJob || isRetryInProgress}
                                style={{
                                    border: '1px solid #cfc3f8',
                                    backgroundColor: '#f3efff',
                                    color: '#5a41c9',
                                    borderRadius: t.radius.md,
                                    padding: '10px 16px',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    cursor: isRefreshing || isActiveJob || isRetryInProgress ? 'not-allowed' : 'pointer',
                                    opacity: isRefreshing || isActiveJob || isRetryInProgress ? 0.7 : 1,
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
                                }}
                            >
                                {isRefreshing ? 'Refreshing...' : 'Refresh'}
                            </button>
                            <EDSButton
                                variant="secondary"
                                type="button"
                                label={isRetryingJob ? 'Retrying...' : 'Retry'}
                                onEDSButtonClick={retryJob}
                                isDisabled={isRetryingJob || isActiveJob || isCancellingJob}
                            />
                            <EDSButton
                                variant="primary"
                                type="button"
                                label={isCancellingJob ? 'Cancelling...' : 'Cancel'}
                                onEDSButtonClick={cancelJob}
                                isDisabled={isCancellingJob || (!isActiveJob && !isRetryInProgress)}
                            />
                        </div>
                    </div>
 
                    {/* Progress card */}
                    <div style={{ ...card, marginBottom: '20px' }}>
                        <div style={cardHeader}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: t.color.textPrimary }}>Progress</span>
                        </div>
                        <div style={{ padding: '24px', backgroundColor: t.color.surfaceMuted, borderBottomLeftRadius: t.radius.lg, borderBottomRightRadius: t.radius.lg }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '64px', marginBottom: '22px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '26px', fontWeight: 700, color: t.color.textPrimary }}>{job.total}</div>
                                    <div style={{ fontSize: '12px', color: t.color.textSecondary, marginTop: '2px' }}>Total</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '26px', fontWeight: 700, color: t.color.success }}>{job.copied}</div>
                                    <div style={{ fontSize: '12px', color: t.color.textSecondary, marginTop: '2px' }}>Copied</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '26px', fontWeight: 700, color: t.color.danger }}>{failedTotal}</div>
                                    <div style={{ fontSize: '12px', color: t.color.textSecondary, marginTop: '2px' }}>Failed</div>
                                </div>
                            </div>
 
                            {/* Segmented progress bar: green = copied, red = failed */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div
                                    role="progressbar"
                                    aria-valuenow={copiedPct}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    style={{
                                        flex: 1,
                                        height: '8px',
                                        borderRadius: '999px',
                                        backgroundColor: t.color.border,
                                        overflow: 'hidden',
                                        display: 'flex'
                                    }}
                                >
                                    <div style={{ width: `${copiedPct}%`, backgroundColor: t.color.success }} />
                                    <div style={{ width: `${failedPct}%`, backgroundColor: t.color.danger }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: t.color.textPrimary, minWidth: '38px', textAlign: 'right' }}>
                                    {copiedPct}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {showFailureReason && (
                        <div
                            style={{
                                ...card,
                                borderColor: t.color.danger,
                                backgroundColor: t.color.dangerTint,
                                color: t.color.danger,
                                padding: '12px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '18px'
                            }}
                        >
                            Failure reason: {failureReasonText}
                            {nextSteps.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <div>Next steps:</div>
                                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                        {nextSteps.map((step, index) => (
                                            <li key={`next-step-${index}`} style={{ marginBottom: '4px' }}>
                                                {step}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
 
                </div>
            </div>
        </div>
    );
};
 
export default JobDetailsPage;